/**
 * ResourceMetersPage - Generic Meters History page for non-aircraft/non-simulator resources
 * (e.g., Instructors, Rooms, Equipment)
 *
 * Uses Symfony API: POST /api/v1/org/{org}/resources/counters-history
 */

import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { formatDateTime } from './utils/dateFormatter';
import {
	Card,
	CardBody,
	CardHeader,
	Button,
	Chip,
	Select,
	SelectItem,
	SelectSection,
	Skeleton,
	addToast
} from '@heroui/react';
import { Gauge, ChevronLeft, ChevronRight, X, Filter } from 'lucide-react';
import ResourcesApi from '../../api/resourcesApi.js';
import HeroUIBreadcrumb from '../breadcrumbs/HeroUIBreadcrumb.js';

// Get organization unique from URL
const getOrgUnique = () => {
	const match = window.location.pathname.match(/\/org\/([^/]+)\//);
	return match ? match[1] : '';
};

// Types to exclude from resource filter (they have dedicated meters pages)
const EXCLUDED_TYPES = ['aircraft', 'simulator'];

function ResourceMetersPage() {
	const { resourceId } = useParams();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const orgUnique = getOrgUnique();

	// All available resources (for filter dropdown)
	const [allResources, setAllResources] = React.useState([]);
	const [resourcesLoading, setResourcesLoading] = React.useState(true);

	// Currently viewed resource (from URL param)
	const [resource, setResource] = React.useState(null);

	// Selected resources for filtering (multi-select)
	const [selectedResourceIds, setSelectedResourceIds] = React.useState(() => {
		// Initialize from URL query params
		const idsParam = searchParams.get('resources');
		if (idsParam) {
			return new Set(idsParam.split(',').filter(Boolean));
		}
		// Default to the resource from URL path
		return resourceId ? new Set([resourceId]) : new Set();
	});

	const [history, setHistory] = React.useState([]);
	const [loading, setLoading] = React.useState(true);

	// Pagination (page is 0-indexed)
	const [page, setPage] = React.useState(0);
	const [hasNextPage, setHasNextPage] = React.useState(false);

	// Fetch all resources for the filter dropdown
	const fetchAllResources = React.useCallback(async () => {
		setResourcesLoading(true);
		try {
			const response = await ResourcesApi.getResources();
			const data = response.data?.data || response.data || response;
			const resources = Array.isArray(data) ? data : data.resources || [];

			// Filter out aircraft and simulators
			const filteredResources = resources.filter(r => {
				const typeName = (r.typeUnique || r.type?.name || '').toLowerCase();
				return !EXCLUDED_TYPES.includes(typeName);
			});

			setAllResources(filteredResources);
		} catch (err) {
			console.error('Failed to load resources:', err);
		} finally {
			setResourcesLoading(false);
		}
	}, []);

	// Fetch single resource data (for header display)
	const fetchResourceData = React.useCallback(async () => {
		if (!resourceId) return;
		try {
			const response = await ResourcesApi.getResourceById(parseInt(resourceId, 10));
			const data = response.data?.data || response.data || response;
			setResource(data);
		} catch (err) {
			console.error('Failed to load resource:', err);
		}
	}, [resourceId]);

	// Fetch counters history
	const fetchHistory = React.useCallback(async () => {
		const resourceIds = Array.from(selectedResourceIds).map(id => parseInt(id, 10));
		if (resourceIds.length === 0) {
			setHistory([]);
			setLoading(false);
			return;
		}

		setLoading(true);
		try {
			const response = await ResourcesApi.getResourcesCountersHistory(resourceIds, { page });
			const data = response.data?.data || response.data || {};

			// Handle Symfony response format
			if (data.countersHistory !== undefined) {
				setHistory(data.countersHistory);
				setHasNextPage(data.next > 0);
			} else if (Array.isArray(data)) {
				setHistory(data);
				setHasNextPage(false);
			} else {
				setHistory([]);
				setHasNextPage(false);
			}
		} catch (err) {
			console.error('Failed to load counters history:', err);
			let errorMessage = 'Failed to load meters history';
			if (err.response?.status === 422) {
				errorMessage = 'Invalid request. Please try again.';
			} else if (err.response?.status === 404) {
				errorMessage = 'Resource not found';
			} else if (err.response?.status === 403) {
				errorMessage = 'You do not have permission to view this resource\'s history';
			} else if (err.message) {
				errorMessage = err.message;
			}
			addToast({
				title: 'Error',
				description: errorMessage,
				color: 'danger'
			});
			setHistory([]);
		} finally {
			setLoading(false);
		}
	}, [selectedResourceIds, page]);

	// Update URL when selection changes
	React.useEffect(() => {
		const ids = Array.from(selectedResourceIds);
		if (ids.length > 0 && !(ids.length === 1 && ids[0] === resourceId)) {
			setSearchParams({ resources: ids.join(',') });
		} else {
			// Clear query params if only showing the default resource
			setSearchParams({});
		}
	}, [selectedResourceIds, resourceId, setSearchParams]);

	// Initial data fetch
	React.useEffect(() => {
		fetchAllResources();
		fetchResourceData();
	}, [fetchAllResources, fetchResourceData]);

	// Fetch history when selection or page changes
	React.useEffect(() => {
		fetchHistory();
	}, [fetchHistory]);

	// Group resources by type for SelectSection
	const resourcesByType = React.useMemo(() => {
		const grouped = {};
		allResources.forEach(r => {
			const typeName = r.type?.name || r.typeUnique || 'Other';
			if (!grouped[typeName]) {
				grouped[typeName] = [];
			}
			grouped[typeName].push(r);
		});
		// Sort types alphabetically, sort resources within each type by name
		const sortedTypes = Object.keys(grouped).sort();
		sortedTypes.forEach(type => {
			grouped[type].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
		});
		return { grouped, sortedTypes };
	}, [allResources]);

	// Handle resource selection change
	const handleResourceSelectionChange = keys => {
		const newSelection = new Set(keys);
		setSelectedResourceIds(newSelection);
		setPage(0); // Reset pagination when filter changes
	};

	// Clear filter (show only the URL resource)
	const handleClearFilter = () => {
		setSelectedResourceIds(resourceId ? new Set([resourceId]) : new Set());
		setPage(0);
	};


	// Back navigation
	const handleBack = () => {
		navigate('/');
	};

	// Navigate to calendar day view when clicking on event row
	const handleEventClick = entry => {
		if (!entry.eventId || !entry.eventStartTime) return;

		// Calculate day timestamp (midnight UTC of the event day)
		const eventDate = new Date(entry.eventStartTime * 1000);
		const dayTimestamp = Math.floor(
			new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate()).getTime() / 1000
		);

		// Format date for URL: YYYY-MM-DD
		const yyyy = eventDate.getFullYear();
		const mm = String(eventDate.getMonth() + 1).padStart(2, '0');
		const dd = String(eventDate.getDate()).padStart(2, '0');
		const dateStr = `${yyyy}-${mm}-${dd}`;

		// Get timezone from serverData or fallback to UTC
		const timezone = window.serverData?.timeZoneId || window.globals?.timeZone?.id || 'UTC';

		// Build calendar day view URL with event parameters
		const url = `/org/${orgUnique}/calendar/day/${dateStr}/${timezone}/#?day=${dayTimestamp}&org=${orgUnique}&action=event-view&event=${entry.eventId}&startTime=${entry.eventStartTime}&finishTime=${entry.eventFinishTime || entry.eventStartTime}`;

		window.open(url, '_blank');
	};

	// Breadcrumbs
	const breadcrumbCustomItems = [
		{ label: 'Resources', href: `/org/${orgUnique}/resources/` }
	];

	// Get selected resource names for header display
	const selectedResourceNames = React.useMemo(() => {
		if (selectedResourceIds.size === 0) return '';
		if (selectedResourceIds.size === 1) {
			const id = Array.from(selectedResourceIds)[0];
			const r = allResources.find(res => String(res.id) === id);
			return r?.name || resource?.name || 'Resource';
		}
		return `${selectedResourceIds.size} Resources`;
	}, [selectedResourceIds, allResources, resource]);

	// Loading skeleton
	if (resourcesLoading && !resource) {
		return (
			<div className="max-w-[1400px] mx-auto px-4 py-8">
				<Skeleton className="h-8 w-48 rounded-lg mb-6" />
				<Skeleton className="h-24 w-full rounded-lg mb-4" />
				<div className="space-y-2">
					{[1, 2, 3].map(i => (
						<Skeleton key={i} className="h-16 w-full rounded-lg" />
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="max-w-[1400px] mx-auto px-4 py-8">
			<HeroUIBreadcrumb currentPage="Meters History" customItems={breadcrumbCustomItems} />

			{/* Page Header */}
			<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
				<div className="flex items-center gap-4">
					<div className="p-3 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600">
						<Gauge className="w-8 h-8 text-white" />
					</div>
					<div>
						<h1 className="text-2xl font-bold">{selectedResourceNames}</h1>
						<p className="text-default-500 text-sm">
							Meters History
						</p>
					</div>
				</div>
				<Button variant="light" isIconOnly onPress={handleBack} className="self-end md:self-auto">
					<X className="w-5 h-5" />
				</Button>
			</div>

			{/* Filter Card */}
			<Card className="mb-6">
				<CardHeader className="pb-0">
					<div className="flex items-center gap-2">
						<Filter className="w-4 h-4 text-default-500" />
						<span className="text-sm font-medium">Filter Resources</span>
					</div>
				</CardHeader>
				<CardBody>
					<div className="flex flex-col sm:flex-row gap-3">
						<Select
							label="Resources"
							placeholder="Select resources"
							aria-label="Select resources to view meters history"
							selectionMode="multiple"
							selectedKeys={selectedResourceIds}
							onSelectionChange={handleResourceSelectionChange}
							className="w-full sm:w-80"
							isLoading={resourcesLoading}
						>
							{resourcesByType.sortedTypes.map(typeName => (
								<SelectSection
									key={typeName}
									title={typeName}
									classNames={{ heading: 'text-default-400 text-xs uppercase' }}
								>
									{resourcesByType.grouped[typeName].map(r => (
										<SelectItem key={String(r.id)}>{r.name}</SelectItem>
									))}
								</SelectSection>
							))}
						</Select>
						{selectedResourceIds.size > 1 && (
							<Button
								size="sm"
								variant="flat"
								startContent={<X className="w-4 h-4" />}
								onPress={handleClearFilter}
								className="self-end sm:self-center"
							>
								Clear
							</Button>
						)}
					</div>
				</CardBody>
			</Card>

			{/* History Table */}
			<Card>
				<CardBody>
					{loading ? (
						<div className="space-y-2">
							{[1, 2, 3].map(i => (
								<Skeleton key={i} className="h-16 w-full rounded-lg" />
							))}
						</div>
					) : selectedResourceIds.size === 0 ? (
						<div className="text-center py-12 text-default-500">
							Select at least one resource to view meters history.
						</div>
					) : history.length === 0 ? (
						<div className="text-center py-12 text-default-500">
							No meter history found for the selected resources.
						</div>
					) : (
						<>
							<div className="overflow-x-auto">
								{/* Event-grouped table matching HeroUI Table styling */}
								<div className="min-w-[900px]">
									{/* Table Header */}
									<div className="grid grid-cols-[140px_140px_120px_80px_80px_80px_minmax(100px,1fr)] gap-0 bg-default-100 text-default-600 text-xs font-semibold uppercase tracking-wider rounded-lg">
										<div className="px-3 py-3 rounded-l-lg">Event Time</div>
										<div className="px-3 py-3">Last Modified</div>
										<div className="px-3 py-3">Resource</div>
										<div className="px-3 py-3 text-right">Start</div>
										<div className="px-3 py-3 text-right">End</div>
										<div className="px-3 py-3 text-right">Total</div>
										<div className="px-3 py-3 rounded-r-lg">Notes</div>
									</div>

									{/* Event Groups */}
									{history.map((entry, entryIndex) => {
										const countersData = entry.counters || [entry];
										const isEvenGroup = entryIndex % 2 === 0;
										const isClickable = entry.eventId && entry.eventStartTime;

										return (
											<div
												key={entry.eventId || entryIndex}
												className={`${entryIndex === 0 ? 'rounded-t-lg' : 'border-t border-default-200'} ${entryIndex === history.length - 1 ? 'rounded-b-lg' : ''} ${isEvenGroup ? '' : 'bg-default-50'} ${isClickable ? 'cursor-pointer hover:bg-default-100 transition-colors' : ''}`}
												onClick={isClickable ? () => handleEventClick(entry) : undefined}
												role={isClickable ? 'button' : undefined}
												tabIndex={isClickable ? 0 : undefined}
												onKeyDown={isClickable ? e => e.key === 'Enter' && handleEventClick(entry) : undefined}
											>
												{countersData.map((counter, counterIndex) => {
													const startValue = counter.startValue ?? (
														(parseFloat(counter.value) || 0) - (parseFloat(counter.diff) || 0)
													).toFixed(2);
													const diff = parseFloat(counter.diff) || 0;
													const isFirstCounter = counterIndex === 0;

													return (
														<div
															key={`${entry.eventId || entryIndex}-${counter.counterId || counter.resourceId || counterIndex}`}
															className={`grid grid-cols-[140px_140px_120px_80px_80px_80px_minmax(100px,1fr)] gap-0 ${
																!isFirstCounter ? 'border-t border-dashed border-default-200' : ''
															}`}
														>
															{/* Event Time */}
															<div className="px-3 py-3 text-sm whitespace-nowrap">
																{isFirstCounter ? (
																	<span className="font-medium text-default-700">
																		{formatDateTime(entry.eventStartTime)}
																	</span>
																) : null}
															</div>

															{/* Last Modified */}
															<div className="px-3 py-3 text-sm whitespace-nowrap text-default-500">
																{isFirstCounter ? formatDateTime(entry.dateOfChange || counter.dateOfChange) : null}
															</div>

															{/* Resource */}
															<div className="px-3 py-3">
																<Chip
																	size="sm"
																	variant="flat"
																	color="default"
																>
																	{counter.counterName || counter.resourceName || '-'}
																</Chip>
															</div>

															{/* Start */}
															<div className="px-3 py-3 text-sm text-right font-mono text-default-600">
																{startValue}
															</div>

															{/* End */}
															<div className="px-3 py-3 text-sm text-right font-mono text-default-600">
																{counter.value ?? '-'}
															</div>

															{/* Total */}
															<div className="px-3 py-3 text-sm text-right font-mono">
																<span className={`font-medium ${diff > 0 ? 'text-success-600' : diff < 0 ? 'text-danger-600' : 'text-default-400'}`}>
																	{diff > 0 ? '+' : ''}{diff.toFixed(2)}
																</span>
															</div>

															{/* Notes */}
															<div className="px-3 py-3 text-sm text-default-500 truncate" title={counter.comment || ''}>
																{isFirstCounter ? (counter.comment || <span className="text-default-300">-</span>) : null}
															</div>
														</div>
													);
												})}
											</div>
										);
									})}
								</div>
							</div>

							{/* Pagination */}
							{(page > 0 || hasNextPage) && (
								<div className="flex justify-center items-center gap-4 mt-4">
									<Button
										size="sm"
										variant="flat"
										isDisabled={page === 0}
										onPress={() => setPage(Math.max(0, page - 1))}
										startContent={<ChevronLeft className="w-4 h-4" />}
									>
										Previous
									</Button>
									<span className="text-sm text-default-500">
										Page {page + 1}
									</span>
									<Button
										size="sm"
										variant="flat"
										isDisabled={!hasNextPage}
										onPress={() => setPage(page + 1)}
										endContent={<ChevronRight className="w-4 h-4" />}
									>
										Next
									</Button>
								</div>
							)}
						</>
					)}
				</CardBody>
			</Card>
		</div>
	);
}

export default ResourceMetersPage;
